import {DATA_TYPES} from "../constants";
import {stateTypeF} from "../state";
import {dataSplit, infoType} from "./Dataset";

export abstract class BaseModel {
	mlDataInfo: infoType;
	lossFunction: (xTest: number[][], yTest: number[]) => number;

	epochsComplete = 0;
	lowestLoss = 1e10;
	maxElements = 50000;
	score = 0;

	constructor(mlDataInfo: infoType) {
		if (mlDataInfo === undefined) {
			return;
		}
		this.mlDataInfo = mlDataInfo;
		if (this.mlDataInfo.outputType === DATA_TYPES.number) {
			this.lossFunction = this.mseLoss;
		} else if (this.mlDataInfo.outputType === DATA_TYPES.categorical) {
			this.lossFunction = this.logLoss;
		}
	}

	abstract modelName: string;
	abstract modelState: number;
	abstract predict(X: number[][]): number[];
	abstract predictProbs(X: number[][]): number[][];
	abstract train(
		trainData: dataSplit,
		state?: stateTypeF,
		callbackFunction?: Function
	): void;

	predictPersona(X: number[][]) {
		const yHat = this.predict(X);
		return yHat[0];
	}

	static setModelProgress(
		progress: number,
		state: stateTypeF,
		modelName: string,
		lastUpdated?: number
	) {
		let modelProgress = state.saveState.modelTraining.modelProgress;
		let lastProgress = modelProgress[modelName];

		var t = new Date().getTime() / 1000;
		const secondsPassed = t - lastUpdated;

		if (
			(secondsPassed > 1 && lastProgress + 1 < progress) ||
			progress === 100 ||
			progress === 0
		) {
			modelProgress[modelName] = progress;
			let col = state.saveState.dataset.getColumn(
				state.saveState.modelTraining.valueToPredict
			);
			if (col === undefined) {
				return;
			}
			state.setTotalTrainProgress(
				state.getCurrentModels(col.columnType),
				state.saveState.modelTraining.modelProgress,
				state.saveState.modelTraining.modelStates
			);
			lastUpdated = new Date().getTime() / 1000;
		}
		return lastUpdated;
	}

	isBestModel(xTest: number[][], yTest: number[]) {
		let loss = this.lossFunction(xTest, yTest);
		if (loss < this.lowestLoss) {
			this.lowestLoss = loss;
			return true;
		} else {
			return false;
		}
	}

	mseLoss(xTest: number[][], yTest: number[]) {
		let yHatAr = this.predict(xTest);
		let mse = 0;
		for (let i = 0; i < yTest.length; i++) {
			mse += Math.sqrt(Math.pow(yHatAr[i] - yTest[i], 2));
		}
		mse /= yTest.length;
		return mse;
	}

	logLoss(xTest: number[][], yTest: number[]) {
		let yHatAr = this.predictProbs(xTest);
		let loss = 0;
		for (let i = 0; i < yHatAr.length; i++) {
			let cl = yTest[i];
			loss += Math.log(Math.max(yHatAr[i][cl], 1e-3));
		}
		loss /= yTest.length;
		return -loss;
	}

	subsampleArray(data: number[][]) {
		let subData: number[][] = [];
		let rowsToKeep = Math.floor(this.maxElements / data[0].length);
		rowsToKeep = Math.min(data.length, rowsToKeep);
		for (let i = 0; i < rowsToKeep; i++) {
			subData.push(data[i]);
		}
		return subData;
	}

	to1DArray(data: number[][]) {
		return data.map((val) => val[0]);
	}
}
